/**
 * Unsaved annotation notes, persisted to localStorage.
 *
 * THIS IS THE REASON ESC IS SAFE. The overlay covers the whole app, so Esc is
 * the reflex for getting out of it — and a user who hits it after typing three
 * notes must not lose them. With every keystroke written here, the only
 * actions that can destroy notes are an explicit Discard and a successful
 * Save. Esc, a reload, a crash, navigating away, and a failed submit all
 * preserve them.
 *
 * `lib/draft.ts` does the same thing for the interview, for the same stated
 * reason. Annotations are the same shape of work: typed, unrecoverable if
 * lost, and cheap to keep.
 *
 * KEYED BY PATH. Notes are placed at coordinates on one screen; restoring
 * `/ideas` notes onto `/idea/abc` would put them over unrelated content and
 * read as a bug. A draft belongs to the screen it was written on.
 *
 * DEVICE-BOUND, like the interview draft — notes started on a phone are not on
 * the laptop.
 */

const KEY = 'ideabrief.annotations.v1';

/** Drafts older than this are dropped on read. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface DraftNote {
  /** Overlay (viewport) coordinates of where typing started. */
  x: number;
  y: number;
  text: string;
}

interface StoredDraft {
  notes: DraftNote[];
  scrollY: number;
  updatedAt: number;
}

type Store = Record<string, StoredDraft>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as Store;
  } catch {
    // A hand-edited or half-written value must not crash the app on load.
    return {};
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Quota or private mode. The overlay still works from memory for this
    // session; only cross-reload recovery is lost, and silently degrading
    // beats an error dialog over a convenience.
  }
}

/** Saves the notes for one screen. An empty list clears that screen's draft. */
export function saveDraftNotes(
  path: string,
  notes: DraftNote[],
  scrollY: number,
): void {
  const store = read();
  // Only notes with real text are worth keeping — an empty caret the user
  // clicked and abandoned is not work, and restoring one would be noise.
  const kept = notes.filter((n) => n.text.trim().length > 0);

  if (kept.length === 0) {
    delete store[path];
  } else {
    store[path] = { notes: kept, scrollY, updatedAt: Date.now() };
  }
  write(store);
}

/** Notes previously stashed for this screen, oldest entries expired. */
export function loadDraftNotes(path: string): DraftNote[] {
  const store = read();
  const entry = store[path];
  if (!entry || !Array.isArray(entry.notes)) return [];

  if (Date.now() - (entry.updatedAt ?? 0) > MAX_AGE_MS) {
    delete store[path];
    write(store);
    return [];
  }

  // Defensive: only well-formed notes survive a hand-edited store.
  return entry.notes.filter(
    (n): n is DraftNote =>
      typeof n?.x === 'number' &&
      typeof n?.y === 'number' &&
      typeof n?.text === 'string' &&
      n.text.trim().length > 0,
  );
}

/** Explicit discard, and the successful-save path. */
export function clearDraftNotes(path: string): void {
  const store = read();
  delete store[path];
  write(store);
}
